/**
 * Système de monitoring d'erreurs pour Cabinet 4.0
 * Capture les erreurs, les log et envoie des notifications
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Configuration
const config = {
  enabled: process.env.NODE_ENV === 'production',
  maxErrorsPerMinute: 10,
  errorQueue: [],
  lastNotificationTime: 0,
  notificationCooldown: 60000, // 1 minute
};

// Compteur d'erreurs pour éviter le spam
let errorCount = 0;
let errorCountResetTime = Date.now();

/**
 * Réinitialise le compteur d'erreurs chaque minute
 */
const resetErrorCountIfNeeded = () => {
  const now = Date.now();
  if (now - errorCountResetTime > 60000) {
    errorCount = 0;
    errorCountResetTime = now;
  }
};

/**
 * Capture les informations de contexte de l'erreur
 */
const captureContext = () => {
  return {
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    localStorage: {
      hasToken: !!localStorage.getItem('token'),
      theme: localStorage.getItem('theme'),
    },
    performance: {
      memory: performance?.memory?.usedJSHeapSize,
      timing: performance?.timing?.loadEventEnd - performance?.timing?.navigationStart,
    },
  };
};

/**
 * Formate l'erreur pour l'envoi
 */
const formatError = (error, errorInfo = null) => {
  return {
    message: error?.message || String(error),
    stack: error?.stack || null,
    componentStack: errorInfo?.componentStack || null,
    name: error?.name || 'Error',
    context: captureContext(),
  };
};

/**
 * Envoie l'erreur au backend pour logging et notification
 */
const sendErrorToBackend = async (errorData) => {
  try {
    await axios.post(`${API_URL}/api/monitoring/error`, errorData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      timeout: 5000,
    });
  } catch (e) {
    // Log local si l'envoi échoue
    console.error('[ErrorMonitoring] Failed to send error to backend:', e);
  }
};

/**
 * Log l'erreur en console avec formatage
 */
const logErrorToConsole = (error, context) => {
  console.group('🚨 Error Captured');
  console.error('Error:', error);
  console.log('Context:', context);
  console.groupEnd();
};

/**
 * Fonction principale de capture d'erreur
 */
export const captureError = (error, errorInfo = null, severity = 'error') => {
  resetErrorCountIfNeeded();
  
  // Rate limiting
  if (errorCount >= config.maxErrorsPerMinute) {
    console.warn('[ErrorMonitoring] Rate limit reached, error not captured');
    return;
  }
  
  errorCount++;
  
  const errorData = {
    ...formatError(error, errorInfo),
    severity,
    source: 'frontend',
    appVersion: window.__BUILD_INFO__?.version || 'unknown',
    buildTime: window.__BUILD_INFO__?.buildTime || 'unknown',
  };
  
  // Log en console (toujours)
  logErrorToConsole(error, errorData.context);
  
  // Envoi au backend (seulement en production)
  if (config.enabled) {
    sendErrorToBackend(errorData);
  }
  
  return errorData;
};

/**
 * Capture les erreurs non gérées
 */
export const setupGlobalErrorHandlers = () => {
  // Erreurs JavaScript non gérées
  window.onerror = (message, source, lineno, colno, error) => {
    captureError(error || new Error(message), null, 'error');
    return false; // Ne pas empêcher le comportement par défaut
  };
  
  // Rejections de Promise non gérées
  window.onunhandledrejection = (event) => {
    captureError(event.reason || new Error('Unhandled Promise Rejection'), null, 'error');
  };
  
  // Erreurs de ressources (images, scripts, etc.)
  window.addEventListener('error', (event) => {
    if (event.target !== window) {
      captureError(
        new Error(`Resource failed to load: ${event.target?.src || event.target?.href}`),
        null,
        'warning'
      );
    }
  }, true);
  
  console.log('[ErrorMonitoring] Global error handlers initialized');
};

/**
 * Capture les erreurs de composants React (Error Boundary)
 */
export const captureComponentError = (error, errorInfo) => {
  captureError(error, errorInfo, 'critical');
};

/**
 * Capture les erreurs d'API
 */
export const captureApiError = (error, endpoint, method) => {
  const apiError = {
    ...error,
    message: `API Error: ${method} ${endpoint} - ${error.message}`,
  };
  captureError(apiError, { endpoint, method }, 'error');
};

/**
 * Capture les événements personnalisés (analytics/monitoring)
 */
export const captureEvent = (eventName, data = {}) => {
  if (!config.enabled) return;
  
  try {
    axios.post(`${API_URL}/api/monitoring/event`, {
      event: eventName,
      data,
      context: captureContext(),
      timestamp: new Date().toISOString(),
    }, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      timeout: 5000,
    });
  } catch (e) {
    console.warn('[ErrorMonitoring] Failed to capture event:', e);
  }
};

/**
 * Health check du frontend
 */
export const performHealthCheck = async () => {
  const checks = {
    localStorage: false,
    apiConnection: false,
    serviceWorker: false,
  };
  
  try {
    // Test localStorage
    localStorage.setItem('health_check', 'test');
    localStorage.removeItem('health_check');
    checks.localStorage = true;
  } catch (e) {
    checks.localStorage = false;
  }
  
  try {
    // Test API connection
    const response = await axios.get(`${API_URL}/api/health`, { timeout: 5000 });
    checks.apiConnection = response.status === 200;
  } catch (e) {
    checks.apiConnection = false;
  }
  
  try {
    // Test Service Worker
    checks.serviceWorker = 'serviceWorker' in navigator && 
      (await navigator.serviceWorker.getRegistrations()).length > 0;
  } catch (e) {
    checks.serviceWorker = false;
  }
  
  return {
    healthy: Object.values(checks).every(Boolean),
    checks,
    timestamp: new Date().toISOString(),
  };
};

export default {
  captureError,
  captureComponentError,
  captureApiError,
  captureEvent,
  setupGlobalErrorHandlers,
  performHealthCheck,
};
